import { createClient, type SupabaseClient } from '@supabase/supabase-js'

/**
 * Supabase browser client for OAuth authentication.
 *
 * This client is used for the canonical Supabase OAuth flow:
 * 1. signInWithOAuth() - initiates OAuth
 * 2. exchangeCodeForSession() - exchanges code for tokens
 * 3. Tokens are then POSTed to /v1/auth/cookie-handoff to set HttpOnly cookies
 *
 * Environment variables required:
 * - VITE_SUPABASE_URL: Supabase project URL (e.g., https://xxx.supabase.co)
 * - VITE_SUPABASE_ANON_KEY: Supabase anonymous key
 */
const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').trim()
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim()

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '[Supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. ' +
    'OAuth will not work. Set these in your .env file.'
  )
}

function computeSiteOrigin(): string | undefined {
  const envSiteOrigin = import.meta.env.VITE_SITE_ORIGIN
  const envServiceLayer = import.meta.env.VITE_SERVICE_LAYER_BASE_URL

  const preferLocal =
    !envSiteOrigin &&
    (envServiceLayer === '/service' ||
      (envServiceLayer || '').includes('localhost') ||
      (envServiceLayer || '').includes('127.0.0.1'))

  if (preferLocal) {
    return 'http://localhost:5173'
  }

  if (envSiteOrigin) {
    return envSiteOrigin.replace(/\/$/, '')
  }

  if (typeof window !== 'undefined') {
    return window.location.origin.replace(/\/$/, '')
  }

  return undefined
}

const stubClient = {
  auth: {
    getSession: () => Promise.resolve({ data: { session: null }, error: null }),
    getUser: () => Promise.resolve({ data: { user: null }, error: null }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
    signInWithOAuth: () => Promise.resolve({ data: { provider: null, url: null }, error: new Error('Supabase not configured') }),
    signOut: () => Promise.resolve({ error: null }),
    exchangeCodeForSession: () => Promise.resolve({ data: { session: null, user: null }, error: new Error('Supabase not configured') }),
  },
  from: () => ({ select: () => ({ single: () => Promise.resolve({ data: null, error: null }) }) }),
} as unknown as SupabaseClient

export const supabase: SupabaseClient = (supabaseUrl && supabaseAnonKey)
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        storageKey: 'sb-clanda-app',
        redirectTo: (() => {
          const origin = computeSiteOrigin()
          return origin ? `${origin}/auth/callback` : undefined
        })(),
      },
    } as any)
  : stubClient

